import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash } from '@phosphor-icons/react';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishlistPage = () => {
  const { storeSlug } = useParams();
  const { wishlistIds, toggleWishlist, fetchWishlistBySlug } = useWishlist();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (storeSlug) fetchWishlistBySlug(storeSlug);
  }, [storeSlug, fetchWishlistBySlug]);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/s/${storeSlug}`);
        setStoreData(res.data);
      } catch (e) { console.error('Failed to load store:', e); }
    };
    fetchStore();
  }, [storeSlug]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!storeData || wishlistIds.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/stores/${storeData.id}/wishlist`, { withCredentials: true });
        setProducts(res.data.products || []);
      } catch {
        setProducts([]);
      }
      setLoading(false);
    };
    fetchProducts();
  }, [storeData, wishlistIds]);

  const handleRemove = async (productId) => {
    if (storeData?.id) {
      await toggleWishlist(storeData.id, productId);
    }
  };

  const handleAddToCart = (product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.sale_price || product.price,
      image: product.images?.[0],
      quantity: 1,
      slug: product.slug
    });
  };

  const colors = storeData?.theme_config?.colors || { primary: '#c2185b' };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 text-center" data-testid="wishlist-empty">
        <Heart size={56} className="mx-auto mb-4" style={{ color: colors.primary }} weight="light" />
        <h2 className="text-xl font-bold mb-2">Aucun favori</h2>
        <p className="text-slate-500 mb-6">Vous n'avez pas encore ajouté de produits en favoris.</p>
        <Link to={`/store/${storeSlug}`}>
          <Button style={{ backgroundColor: colors.primary }} className="text-white hover:opacity-90">
            Parcourir la boutique
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="wishlist-page">
      <h2 className="text-xl font-bold mb-6">Mes favoris ({products.length})</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {products.map(product => (
          <div key={product.id} className="bg-white rounded-xl border border-slate-100 p-4 flex gap-4 group hover:shadow-md transition-shadow" data-testid={`wishlist-item-${product.id}`}>
            <Link to={`/store/${storeSlug}/product/${product.slug}`} className="w-24 h-24 flex-shrink-0">
              <img
                src={product.images?.[0] || '/placeholder.png'}
                alt={product.name}
                className="w-full h-full object-cover rounded-lg"
              />
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/store/${storeSlug}/product/${product.slug}`}>
                <h3 className="font-semibold text-sm line-clamp-2 hover:underline">{product.name}</h3>
              </Link>
              <div className="mt-1 flex items-center gap-2">
                {product.sale_price ? (
                  <>
                    <span className="font-bold text-base" style={{ color: colors.primary }}>{product.sale_price.toFixed(2)} EUR</span>
                    <span className="text-xs text-slate-400 line-through">{product.price.toFixed(2)} EUR</span>
                  </>
                ) : (
                  <span className="font-bold text-base">{product.price?.toFixed(2)} EUR</span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  size="sm"
                  className="text-xs h-8 text-white hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                  onClick={() => handleAddToCart(product)}
                  data-testid={`wishlist-add-cart-${product.id}`}
                >
                  <ShoppingCart size={14} className="mr-1" /> Ajouter au panier
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 text-red-500 border-red-200 hover:bg-red-50"
                  onClick={() => handleRemove(product.id)}
                  data-testid={`wishlist-remove-${product.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistPage;
